import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, resolveMediaUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ description: '', file: null });
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerType, setComposerType] = useState('media');
  const [textPostValue, setTextPostValue] = useState('');
  const [textPosts, setTextPosts] = useState([]);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [error, setError] = useState('');
  const { t } = useLanguage();
  const navigate = useNavigate();

  const fetchPosts = async (nextPage = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/getPosts', { params: { page: nextPage, limit: 10 } });
      const nextPosts = response.data?.posts || [];
      setPosts((prev) => (nextPage === 1 ? nextPosts : [...prev, ...nextPosts]));
      setHasMore(nextPage < (response.data?.totalPages || 1));
      setPage(nextPage);
    } catch (err) {
      setError(t('feed.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(1);
  }, []);

  const fetchTextPosts = async () => {
    try {
      const response = await api.get('/textPost/getAll');
      if (Array.isArray(response.data)) {
        setTextPosts(response.data);
      } else {
        setTextPosts(response.data?.posts || []);
      }
    } catch (err) {
      setTextPosts([]);
    }
  };

  useEffect(() => {
    fetchTextPosts();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.description || !form.file) {
      setError(t('feed.errorMissing'));
      return;
    }
    try {
      const formData = new FormData();
      formData.append('description', form.description);
      formData.append('media', form.file);
      await api.post('/addPost', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm({ description: '', file: null });
      setComposerOpen(false);
      await fetchPosts(1);
    } catch (err) {
      setError(err?.response?.data?.message || t('feed.errorShare'));
    }
  };

  const handleTextPost = async (event) => {
    event.preventDefault();
    setError('');
    if (!textPostValue.trim()) {
      setError(t('feed.errorTextEmpty'));
      return;
    }
    try {
      await api.post('/textPost/create', { text: textPostValue });
      setTextPostValue('');
      setComposerOpen(false);
      fetchTextPosts();
    } catch (err) {
      setError(t('feed.errorTextShare'));
    }
  };

  const handleComment = async (postId) => {
    const text = commentDrafts[postId]?.trim();
    if (!text) return;
    try {
      const response = await api.post(`/textPost/comment/${postId}`, { text });
      const updated = response.data?.post;
      if (updated) {
        setTextPosts((prev) => prev.map((p) => (p._id === postId ? updated : p)));
      }
      setCommentDrafts((prev) => ({ ...prev, [postId]: '' }));
    } catch (err) {
      // ignore
    }
  };

  const toggleLike = async (post) => {
    const liked = post.likes?.some((like) => `${like.likedBy}` === `${user?.id}`);
    try {
      if (liked) {
        await api.post(`/unlike/${post._id}`);
      } else {
        await api.post(`/like/${post._id}`);
      }
      fetchPosts(page);
    } catch (err) {
      setError(t('feed.errorLike'));
    }
  };

  const headerSubtitle = useMemo(
    () => `${user?.name || ''} · ${t('feed.subtitle')}`,
    [user?.name, t]
  );

  return (
    <div className="list">
      <div className="card">
        <h2 className="section-title">{t('feed.title')}</h2>
        <p className="section-subtitle">{headerSubtitle}</p>
        {error ? <p style={{ color: 'var(--danger)' }}>{error}</p> : null}
      </div>

      {posts.map((post) => {
        const mediaUrl = resolveMediaUrl(post.mediaType === 'video' ? post.video : post.image);
        const liked = post.likes?.some((like) => `${like.likedBy}` === `${user?.id}`);
        const avatarUrl = resolveMediaUrl(post.userId?.userImage);
        return (
          <div key={post._id} className="card list feed-card">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div className="brand-mark" style={{ background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)' }}>
                {post.userId?.nickName?.slice(0, 1)?.toUpperCase() || 'A'}
              </div>
              {avatarUrl ? (
                <img src={avatarUrl} alt={post.userId?.nickName} style={{ width: 42, height: 42, borderRadius: 14 }} />
              ) : null}
              <div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: 0 }}
                  onClick={() => {
                    if (post.userId?._id === user?.id) {
                      navigate('/profil');
                    } else {
                      navigate(`/kullanici/${post.userId?._id}`);
                    }
                  }}
                >
                  <strong>{post.userId?.nickName || 'Aphel'}</strong>
                </button>
                <div className="muted" style={{ fontSize: 12 }}>
                  {new Date(post.date).toLocaleString()}
                </div>
              </div>
            </div>
            <p>{post.description}</p>
            <div className="media feed-media">
              {post.mediaType === 'video' ? (
                <video src={mediaUrl} controls />
              ) : (
                <img src={mediaUrl} alt="post media" />
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button className="btn btn-ghost" type="button" onClick={() => toggleLike(post)}>
                {liked ? t('feed.liked') : t('feed.like')}
              </button>
              <span className="muted">{post.likes?.length || 0} {t('feed.likes')}</span>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={async () => {
                  const url = `${window.location.origin}/gonderi/${post._id}`;
                  if (navigator.share) {
                    await navigator.share({ title: 'Aphel', url, text: post.description || '' });
                  } else if (navigator.clipboard) {
                    await navigator.clipboard.writeText(url);
                  }
                }}
              >
                {t('feed.shareLabel')}
              </button>
              <button className="btn btn-ghost" type="button" onClick={() => navigate(`/gonderi/${post._id}`, { state: { post, type: 'media' } })}>
                {t('actions.details')}
              </button>
            </div>
          </div>
        );
      })}

      {textPosts.length ? (
        <div className="card list feed-card">
          <h3 className="section-title">{t('feed.textPosts')}</h3>
          {textPosts.map((post) => {
            const avatarUrl = resolveMediaUrl(post.userId?.userImage);
            return (
              <div key={post._id} className="card list" style={{ background: 'rgba(15,23,42,0.7)' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={post.userId?.nickName} style={{ width: 38, height: 38, borderRadius: 12 }} />
                  ) : null}
                  <div>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: 0 }}
                      onClick={() => {
                        if (post.userId?._id === user?.id) {
                          navigate('/profil');
                        } else {
                          navigate(`/kullanici/${post.userId?._id}`);
                        }
                      }}
                    >
                      <strong>{post.userId?.nickName}</strong>
                    </button>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {new Date(post.date).toLocaleString()}
                    </div>
                  </div>
                </div>
                <p>{post.text}</p>
                <div className="list">
                  {(post.comments || []).map((comment) => (
                    <div key={comment._id} className="comment">
                      <div className="comment-meta">@{comment.userId?.nickName || 'Aphel'}</div>
                      <div>{comment.text}</div>
                    </div>
                  ))}
                </div>
                <div className="list">
                  <input
                    className="input"
                    placeholder={t('feed.commentPlaceholder')}
                    value={commentDrafts[post._id] || ''}
                    onChange={(event) =>
                      setCommentDrafts((prev) => ({ ...prev, [post._id]: event.target.value }))
                    }
                  />
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" type="button" onClick={() => handleComment(post._id)}>
                      {t('feed.addComment')}
                    </button>
                    <button className="btn btn-ghost" type="button" onClick={() => navigate(`/gonderi/${post._id}`, { state: { post, type: 'text' } })}>
                      {t('actions.details')}
                    </button>
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={async () => {
                        const url = `${window.location.origin}/gonderi/${post._id}`;
                        if (navigator.share) {
                          await navigator.share({ title: 'Aphel', url, text: post.text || '' });
                        } else if (navigator.clipboard) {
                          await navigator.clipboard.writeText(url);
                        }
                      }}
                    >
                      {t('feed.shareLabel')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {hasMore ? (
        <button className="btn btn-ghost" type="button" onClick={() => fetchPosts(page + 1)} disabled={loading}>
          {loading ? t('common.loading') : t('actions.loadMore')}
        </button>
      ) : null}

      <button type="button" className="fab" onClick={() => setComposerOpen(true)}>
        +
      </button>

      {composerOpen ? (
        <div className="modal-backdrop" onClick={() => setComposerOpen(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h3 className="section-title">{t('actions.createPost')}</h3>
            <div className="toggle-row">
              <button
                type="button"
                className={`toggle-btn ${composerType === 'media' ? 'active' : ''}`}
                onClick={() => setComposerType('media')}
              >
                {t('feed.mediaTab')}
              </button>
              <button
                type="button"
                className={`toggle-btn ${composerType === 'text' ? 'active' : ''}`}
                onClick={() => setComposerType('text')}
              >
                {t('feed.textTab')}
              </button>
            </div>
            {composerType === 'media' ? (
              <form onSubmit={handleSubmit} className="list">
                <div>
                  <label className="muted">{t('feed.description')}</label>
                  <textarea
                    className="input"
                    rows="3"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="muted">{t('feed.media')}</label>
                  <input
                    className="input"
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => setForm((prev) => ({ ...prev, file: e.target.files[0] }))}
                  />
                </div>
                <button className="btn btn-primary" type="submit">
                  {t('actions.sharePost')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleTextPost} className="list">
                <div>
                  <label className="muted">{t('feed.textLabel')}</label>
                  <textarea
                    className="input"
                    rows="4"
                    value={textPostValue}
                    onChange={(e) => setTextPostValue(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary" type="submit">
                  {t('actions.sharePost')}
                </button>
              </form>
            )}
            <button type="button" className="btn btn-ghost" onClick={() => setComposerOpen(false)}>
              {t('actions.close')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
